import { dayEndMood, moodColor, moodLabel, signedFigure, signedMean } from '@adhd/shared';
import { Fragment } from 'react';

/**
 * "How this day went — −3 Rough · −1.4 average of 4", for a closed fold in the Log.
 *
 * ITS OWN FILE, so it can be rendered by a test.
 *
 * It was a local function inside History.tsx, which imports react-router, the
 * API client and eight components; nothing could render it without standing up
 * most of the app, so nothing did, and it went a release printing a different
 * verdict on a day than the strip on Today printed for the same day. The
 * mechanical guard written for that first was a regex over the source, and it
 * matched the COMMENT describing the old bug, then three honest call sites —
 * a chart dot coloured by the mean it is plotted at, and a sentence that says
 * "on average" in the same breath. A guard needing three exceptions is
 * guarding a shape, not a behaviour.
 *
 * So: no router, no fetch, no context. React and @adhd/shared, which esbuild
 * can bundle in isolation — see scripts/check-day-end.mjs, which renders this
 * to a string and reads the words out of it.
 *
 * @param moodsNewestFirst the day's check-in moods, latest first — the order
 *   History's own entry list is already in.
 */
export function dayHeading(moodsNewestFirst: number[]): JSX.Element {
  const mean =
    moodsNewestFirst.length > 0
      ? moodsNewestFirst.reduce((a, m) => a + m, 0) / moodsNewestFirst.length
      : null;

  /*
   * WHERE THE DAY ENDED, because that is what the strip on Today says.
   *
   * The caller hands these over newest-first, so [0] is the last check-in of
   * the day. The server picks `lastMood` with `ORDER BY recorded_at` ascending
   * over the same `day` column and takes the final row — the same row, so the
   * two screens cannot disagree while both go through `dayEndMood`.
   *
   * They DID disagree, because they answered different questions in the same
   * grammar. The strip moved off the mean in release 6 for the reason at
   * status.ts:125 — a mean cannot tell a day that started badly and recovered
   * from one that ended in the low, and those are opposite experiences. This
   * label was never moved with it, so a day read "−3 / Rough" on Today and
   * "A bit low · −1.4 average" in the Log, with nothing on either screen
   * saying which was which. The figure a person keeps is the one they saw
   * first.
   */
  const ended = dayEndMood({ lastMood: moodsNewestFirst[0] ?? null, meanMood: mean });

  /*
   * Built as a list and joined, rather than punctuation written inline.
   *
   * Inline, every separator had to know what might or might not follow it: a
   * trailing " · " dangled on a day with no check-ins, and a leading " — "
   * instead produced two dashes on a day that had both. A list has one rule —
   * " · " between the parts that exist, " — " in front only if there are any —
   * and it cannot dangle.
   */
  const parts: JSX.Element[] = [];
  if (ended !== null) {
    parts.push(
      <span key="mood" style={{ color: moodColor(ended) }}>
        {signedFigure(ended)} {moodLabel(ended)}
      </span>,
    );
  }
  /*
   * The mean stays, demoted, and only where it adds something.
   *
   * He asked for an average on this label and it is still the thing a fold
   * over four check-ins should say beyond the last of them. But it no longer
   * leads, and it no longer carries the colour or the word, so it cannot be
   * read as the day's verdict: it is the second clause, in the muted ink. On a
   * one-check-in day it would be the same number twice, so it is not drawn.
   *
   * NO COUNT ON IT. It read "-1 average of 3" for one render, which is both
   * ambiguous — "average of 3" parses as an average OF the number three — and
   * redundant, because "3 check-ins" is already printed directly above it
   * under the date. Seen in a screenshot, not in the assertions, which asked
   * whether the words were right and not whether the line read well.
   */
  if (mean !== null && moodsNewestFirst.length > 1) {
    parts.push(
      <span key="avg" className="muted">
        {signedMean(mean)} average
      </span>,
    );
  }

  return (
    <>
      How this day went
      {parts.length > 0 && ' — '}
      {parts.map((p, i) => (
        <Fragment key={i}>
          {i > 0 && ' · '}
          {p}
        </Fragment>
      ))}
    </>
  );
}
